import { createClient, SupabaseClient, AuthError } from "@supabase/supabase-js";

// ============================================================
// ENVIRONMENT VARIABLE VALIDATION
// ============================================================

// NOTE: This project has migrated to Supabase's new API key system on the
// server side (Edge Functions now receive sb_secret_... via
// SUPABASE_SERVICE_ROLE_KEY). For the browser client below, either the
// legacy anon JWT or the new sb_publishable_... key works with
// createClient() as long as "Legacy anon, service_role API keys" remains
// enabled for this project in Supabase Dashboard > Project Settings > API
// Keys. If legacy keys are ever fully disabled, update
// VITE_SUPABASE_ANON_KEY to the sb_publishable_... value shown at the top
// of that page — no code change needed here, just the env var value.
const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || "").trim();
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || "").trim();

// Validate required environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error("❌ Supabase environment variables are missing!");
  console.error("  VITE_SUPABASE_URL:", supabaseUrl ? "✅ Set" : "❌ Missing");
  console.error("  VITE_SUPABASE_ANON_KEY:", supabaseAnonKey ? "✅ Set" : "❌ Missing");

  // In production, throw error to prevent runtime issues
  if (import.meta.env.PROD) {
    throw new Error("Supabase configuration is missing. Please check your environment variables.");
  }
}

// ============================================================
// OAUTH CONFIGURATION
// ============================================================

// Get redirect URL from environment or use current origin
const redirectUrl = import.meta.env.VITE_REDIRECT_URL || 
                     window?.location?.origin || 
                     "http://localhost:5173";

// OAuth provider configurations
export const oauthConfig = {
  google: {
    provider: "google" as const,
    options: {
      redirectTo: redirectUrl,
      queryParams: {
        access_type: "offline",
        prompt: "consent",
      },
    },
  },
  facebook: {
    provider: "facebook" as const,
    options: {
      redirectTo: redirectUrl,
      queryParams: {
        display: "popup",
      },
    },
  },
};

// ============================================================
// SUPABASE CLIENT
// ============================================================

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: "pkce", // Recommended for OAuth
  },
  global: {
    headers: {
      "x-application-name": "nation-ubuntu-school",
    },
  },
});

// ============================================================
// TYPES
// ============================================================

export type UserRole = "admin" | "staff" | "teacher" | "guardian";

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  must_change_password: boolean;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Student {
  id: string;
  full_name: string;
  date_of_birth: string | null;
  gender: "female" | "male" | null;
  level: "preschool" | "primary" | "secondary" | null;
  guardian_name: string | null;
  guardian_phone: string | null;
  guardian_email: string | null;
  guardian_id: string | null;
  photo_url: string | null;
  id_doc_url: string | null;
  reference_code: string | null;
  status: "pending" | "approved" | "rejected";
  class_id: string | null;
  shift: "morning" | "afternoon" | null;
  school_id?: string | null;
  academic_year?: number | null;
  enrollment_status?: "active" | "dropped_out" | "graduated" | "transferred";
}

export interface Volunteer {
  id: string;
  full_name: string;
  department: string | null;
  shift: "morning" | "afternoon" | null;
  housekeeping_dept: string | null;
  category: "academic" | "housekeeping" | null;
}

export interface SchoolClass {
  id: string;
  name: string;
  level: "preschool" | "primary" | "secondary";
  shift: "morning" | "afternoon";
  teacher_name: string;
  assistant_name: string;
  school_id?: string | null;
  grade?: string | null;
  section?: string | null;
  academic_year?: number | null;
}

export interface AttendanceRecord {
  id: string;
  record_date: string;
  person_type: "student" | "volunteer";
  person_id: string | null;
  person_name: string;
  status: "present" | "absent";
  justification: "justified" | "not_justified" | null;
  gender: "female" | "male" | null;
  class_name: string | null;
  level: "preschool" | "primary" | "secondary" | null;
  department: string | null;
  shift: "morning" | "afternoon" | null;
}

export interface StoreEntry {
  id: string;
  item: string;
  description: string;
  quantity: number;
  entry_date: string;
}

export interface StoreExit {
  id: string;
  item: string;
  description: string;
  quantity: number;
  destination: string;
  exit_date: string;
}

export interface FoodRecord {
  id: string;
  section: "ubuntu" | "eagles" | "bakery";
  item: string;
  quantity: string;
  details: string | null;
  collector: string | null;
  record_date: string;
}

export interface MediaEntry {
  id: string;
  item: string;
  description: string;
  quantity: number;
  entry_date: string;
}

export interface MediaExit {
  id: string;
  item: string;
  description: string;
  quantity: number;
  exit_date: string;
}

export interface Message {
  id: string;
  recipient: string;
  sender_name: string;
  contact_info: string;
  body: string;
  created_at: string;
}

export interface School {
  id: string;
  name: string;
  location: string | null;
  description: string | null;
  created_at: string;
}

export interface ActivityLog {
  id: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  entity_name: string | null;
  school_id: string | null;
  details: string | null;
  created_at: string;
}

export interface StoreTransfer {
  id: string;
  from_school_id: string | null;
  to_school_id: string | null;
  item: string;
  description: string | null;
  quantity: number;
  status: "pending" | "completed" | "cancelled";
  transferred_by: string | null;
  transfer_date: string;
  created_at: string;
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Check if Supabase is configured properly
 */
export function isSupabaseConfigured(): boolean {
  return !!supabaseUrl && !!supabaseAnonKey;
}

/**
 * Get the current user's profile
 */
export async function getCurrentProfile(): Promise<Profile | null> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error("❌ Error getting user:", userError?.message);
      return null;
    }

    const { data: profile, error: profileError } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .maybeSingle();

    if (profileError) {
      console.error("❌ Error getting profile:", profileError.message);
      return null;
    }

    return profile as Profile;
  } catch (error) {
    console.error("❌ Unexpected error getting profile:", error);
    return null;
  }
}

/**
 * Update user profile
 */
export async function updateProfile(
  userId: string,
  updates: Partial<Profile>
): Promise<{ data: Profile | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error updating profile:", error.message);
      return { data: null, error: error.message };
    }

    return { data: data as Profile, error: null };
  } catch (error) {
    console.error("❌ Unexpected error updating profile:", error);
    return { data: null, error: "Failed to update profile" };
  }
}

/**
 * Log user activity
 */
export async function logActivity(
  action: string,
  entityType?: string,
  entityId?: string,
  entityName?: string,
  schoolId?: string,
  details?: string
): Promise<void> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const user = userData.user;
    if (!user) {
      console.warn("⚠️ No user found for activity logging");
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name, email")
      .eq("id", user.id)
      .maybeSingle();

    await supabase.from("activity_logs").insert({
      user_id: user.id,
      user_name: profile?.full_name || user.email || "",
      user_email: user.email || "",
      action,
      entity_type: entityType || null,
      entity_id: entityId || null,
      entity_name: entityName || null,
      school_id: schoolId || null,
      details: details || null,
    });
  } catch (error) {
    console.error("❌ Error logging activity:", error);
  }
}

// ============================================================
// AUTH HELPER FUNCTIONS
// ============================================================

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<{ data: any; error: AuthError | null }> {
  try {
    const result = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (result.error) {
      console.error("❌ Sign in error:", result.error.message);
    }

    return result;
  } catch (error) {
    console.error("❌ Unexpected sign in error:", error);
    return { data: null, error: error as AuthError };
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("❌ Sign out error:", error.message);
    }
    return { error };
  } catch (error) {
    console.error("❌ Unexpected sign out error:", error);
    return { error: error as AuthError };
  }
}

/**
 * Change user password
 */
export async function changePassword(
  newPassword: string
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      console.error("❌ Password change error:", error.message);
    }

    return { error };
  } catch (error) {
    console.error("❌ Unexpected password change error:", error);
    return { error: error as AuthError };
  }
}

/**
 * Reset password (send reset email)
 */
export async function resetPassword(
  email: string
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error("❌ Password reset error:", error.message);
    }

    return { error };
  } catch (error) {
    console.error("❌ Unexpected password reset error:", error);
    return { error: error as AuthError };
  }
}

// ============================================================
// OAUTH HELPER FUNCTIONS
// ============================================================

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signInWithOAuth(oauthConfig.google);
    if (error) {
      console.error("❌ Google sign in error:", error.message);
    }
    return { error };
  } catch (error) {
    console.error("❌ Unexpected Google sign in error:", error);
    return { error: error as AuthError };
  }
}

/**
 * Sign in with Facebook
 */
export async function signInWithFacebook(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signInWithOAuth(oauthConfig.facebook);
    if (error) {
      console.error("❌ Facebook sign in error:", error.message);
    }
    return { error };
  } catch (error) {
    console.error("❌ Unexpected Facebook sign in error:", error);
    return { error: error as AuthError };
  }
}

// ============================================================
// EXPORT CONFIGURATION
// ============================================================

// Export configuration for debugging (safe version — no key values, only
// presence booleans)
export const supabaseConfig = {
  isConfigured: isSupabaseConfigured(),
  hasUrl: !!supabaseUrl,
  hasAnonKey: !!supabaseAnonKey,
  redirectUrl,
  isProduction: import.meta.env.PROD,
  environment: import.meta.env.MODE,
  oauthProviders: {
    google: !!oauthConfig.google,
    facebook: !!oauthConfig.facebook,
  },
};

// Log configuration status in development only — never logs key values,
// only whether they are present.
if (import.meta.env.DEV) {
  console.log("🔧 Supabase Configuration:");
  console.log(`  URL: ${supabaseUrl ? "✅" : "❌"} ${supabaseUrl || "Not set"}`);
  console.log(`  Anon Key: ${supabaseAnonKey ? "✅ Set" : "❌ Not set"}`);
  console.log(`  Redirect URL: ${redirectUrl}`);
  console.log(`  Environment: ${import.meta.env.MODE}`);
  console.log(`  OAuth Providers: Google: ${oauthConfig.google ? "✅" : "❌"}, Facebook: ${oauthConfig.facebook ? "✅" : "❌"}`);
}

// ============================================================
// ERROR BOUNDARY FOR SUPABASE
// ============================================================

/**
 * Handle Supabase errors gracefully
 */
export function handleSupabaseError(error: any): string {
  if (!error) return "An unknown error occurred";
  
  if (typeof error === "string") return error;
  
  if (error.message) {
    // Common Supabase error messages
    const messages: Record<string, string> = {
      "Invalid login credentials": "Invalid email or password. Please try again.",
      "Email not confirmed": "Please verify your email address before logging in.",
      "User already registered": "An account with this email already exists.",
      "Password should be at least 6 characters": "Password must be at least 6 characters.",
      "Invalid email": "Please enter a valid email address.",
      "Email rate limit exceeded": "Too many attempts. Please wait a moment before trying again.",
    };
    
    return messages[error.message] || error.message;
  }
  
  return "An unexpected error occurred. Please try again.";
}
